/**
 * Constants for the discount flow.
 *
 * Copy lives here rather than in the views for the same reason the rules do: the POS modal, the
 * mobile sheet and the kiosk cart all report the same refusals, and three surfaces phrasing one
 * refusal three ways is how a cashier learns to distrust one of them.
 */

/** The merchant's coupon/deal count limit is already reached. */
export const DISCOUNT_LIMIT_KEY = "discount.limitExceeded" as const;

/**
 * The bill did not move.
 *
 * ⚠️ Shown when the pricing engine accepted the entry and priced it at zero — which is a
 * DIFFERENT thing from the partner refusing, and the customer needs to be able to tell them
 * apart. Kept as its own message for that reason; the app's `DISCOUNT_NOT_APPLICABLE` constant
 * in `utils/constants` is the same string and the two are pinned by a test.
 */
export const DISCOUNT_NOT_APPLICABLE_KEY = "discount.notApplicable" as const;

/** No cart item matches the discount's items or categories. */
export const DISCOUNT_ITEMS_MISSING_KEY = "discount.itemsMissing" as const;

/** A partner refused an apply or a verify without naming a reason. */
export const OFFER_FAILED_KEY = "discount.offerFailed" as const;

/**
 * A partner rejected the OTP.
 *
 * ⚠️ Deliberately not the partner's own `message`: on the loyalty-shaped channel a wrong code
 * arrives as `redemptionInfo.success: false` with no message at all, and the old component
 * hard-coded "Please enter correct otp" for exactly that case. `classifyVerifyResponse` carries
 * the partner's message when there is one and the caller falls back to this.
 */
export const OFFER_OTP_FAILED_KEY = "discount.otpFailed" as const;

/** The unblock call is missing the customer's phone number, which the partner requires. */
export const CUSTOMER_PHONE_MISSING_KEY = "discount.phoneMissing" as const;

/**
 * How many digits a partner's offer OTP has.
 *
 * ⚠️ Not per-partner, unlike the loyalty flow's `redeemOtpLength`: both partners that challenge
 * an offer send six, and neither publishes the length. It is the number of BOXES a dialog draws
 * and what enables its Confirm button, not a validation rule — the backend is the authority on
 * whether a code is right. If a partner ever sends a different count, this becomes a profile
 * field the way `redeemOtpLength` is.
 */
export const OFFER_OTP_LENGTH = 6;

/** A coupon the cashier typed is already on the bill. */
export const COUPON_ALREADY_APPLIED_KEY = "discount.couponAlreadyApplied" as const;

/** A partner offer is on the bill and only one may be — the prompt to swap it. */
export const EXTERNAL_DISCOUNT_REPLACE_MESSAGE =
  "An external discount is already applied. Do you want to replace it?";

/**
 * How many partner offers may sit on one bill.
 *
 * One, and it is a partner constraint rather than a preference: the redemption is recorded
 * against the customer at the partner, so a second offer would need the first released first.
 * `findExternalBillDiscount` is what enforces it.
 */
export const MAX_EXTERNAL_DISCOUNTS_PER_BILL = 1;

/**
 * Which discount lists a sale keeps, and what each is for.
 *
 * `billDiscountList` is what the UI WRITES — the cashier's intent. `discountList` is what the
 * pricing engine ANSWERS with, per discount, including the `netAmount` it worked out. Reading
 * the wrong one is the single most common mistake in this flow: `billDiscountList` always
 * contains what was just pushed, so a check for "did it apply" against it always passes.
 */
export const SALE_DISCOUNT_LISTS = {
  REQUESTED: "billDiscountList",
  PRICED: "discountList",
} as const;
